import { DEFAULT_GROUP_ID } from "@/shared/constants/pvpRoulette"
import type { BetUserData } from "@/shared/api/types/pvpRoulette"
import type { RoundParty, SnapshotBet } from "@/entities/pvp-roulette"

/** Per-row info about a single TELEGRAM_GIFT bet, used to render the thumbnail strip. */
export interface AggregatedGiftBet {
  betId: string
  nftId: string | null
  smallImageUrl: string | null
}

export interface AggregatedUserBets {
  userData: BetUserData
  betIds: string[]
  totalTon: number
  /**
   * Subset of TELEGRAM_GIFT bets in chronological order (oldest first).
   * Empty when the user only bet TON.
   */
  giftBets: AggregatedGiftBet[]
}

export function partyKey(p: RoundParty): string {
  return p.partyId ?? p.userId
}

export function betKey(b: SnapshotBet): string {
  return b.partyId ?? b.userData.userId
}

/** Team-style group: shared party / Team 1 → sub-list per user. Solo → single top-level row. */
export function isTeamParty(party: RoundParty): boolean {
  if (party.partyType === "TEAM") return true
  if (party.partyId === DEFAULT_GROUP_ID || party.userId === DEFAULT_GROUP_ID)
    return true
  return false
}

export function formatPartyHeaderName(
  party: RoundParty,
  isMeSolo: boolean,
  telegramUser: { firstName: string | null; username: string | null }
): string {
  if (party.partyId === DEFAULT_GROUP_ID || party.userId === DEFAULT_GROUP_ID)
    return DEFAULT_GROUP_ID
  if (party.username) return party.username
  if (isMeSolo) {
    return telegramUser.firstName || telegramUser.username || "You"
  }
  return "Player"
}

export function formatUserDisplayName(
  userData: BetUserData,
  isMe: boolean,
  telegramUser: { firstName: string | null; username: string | null }
): string {
  if (userData.username) return userData.username
  if (isMe) {
    return telegramUser.firstName || telegramUser.username || "You"
  }
  return "Player"
}

function mergeUserDataFromBets(bets: SnapshotBet[]): BetUserData {
  const first = bets[0].userData
  let photoUrl = first.photoUrl
  let username = first.username
  for (const b of bets) {
    if (!photoUrl && b.userData.photoUrl) photoUrl = b.userData.photoUrl
    if (!username && b.userData.username) username = b.userData.username
  }
  return { userId: first.userId, username, photoUrl }
}

export function aggregateBetsByUser(bets: SnapshotBet[]): AggregatedUserBets[] {
  const map = new Map<string, { bets: SnapshotBet[] }>()
  for (const b of bets) {
    const uid = b.userData.userId
    const cur = map.get(uid)
    if (cur) cur.bets.push(b)
    else map.set(uid, { bets: [b] })
  }
  const rows: AggregatedUserBets[] = []
  for (const { bets: userBets } of map.values()) {
    userBets.sort((a, b) => {
      const ta = a.createdAt ?? 0
      const tb = b.createdAt ?? 0
      if (ta !== tb) return ta - tb
      return a.betId.localeCompare(b.betId)
    })
    const totalTon = userBets.reduce(
      (s, b) => s + (parseFloat(b.amount || "0") || 0),
      0
    )
    const giftBets: AggregatedGiftBet[] = userBets
      .filter((b) => b.assetClass === "TELEGRAM_GIFT")
      .map((b) => ({
        betId: b.betId,
        nftId: b.nftId ?? null,
        smallImageUrl: b.smallImageUrl ?? null,
      }))
    rows.push({
      userData: mergeUserDataFromBets(userBets),
      betIds: userBets.map((b) => b.betId),
      totalTon,
      giftBets,
    })
  }
  rows.sort((a, b) => a.userData.userId.localeCompare(b.userData.userId))
  return rows
}

export function userDataFromPlayerBanks(
  party: RoundParty,
  targetUserId: string
): BetUserData | null {
  const row = party.playerBanks?.find(
    (pb) => pb.userData.userId === targetUserId
  )
  return row?.userData ?? null
}
